/**
 * DMC-D2 — Canonical TS deleted-state predicates.
 *
 * Mirrors the SQL helpers `public.is_ticket_deleted` and
 * `public.is_ticket_active` defined in migration
 * `20260508120000_dmc_d2_deleted_state_helpers.sql`.
 *
 * Canonical contract (BOTH layers must stay in sync):
 *
 *   isTicketDeleted(t)  ⟺  t.deleted_at != null  ||  t.status === 'Deleted'
 *   isTicketActive(t)   ⟺  t.deleted_at == null  &&  t.status !== 'Deleted'
 *
 * Both representations are checked because, for any row created BEFORE
 * DMC-D1 + the ghost-row backfill landed, the two columns may have
 * diverged (status='Deleted' but deleted_at IS NULL). Post-DMC-D1 the
 * write primitive guarantees they track each other, but read paths
 * should remain forgiving as defense-in-depth — same posture as the
 * RLS policy update in DMC-D2.
 *
 * USAGE:
 *
 *   import { isTicketActive, isTicketDeleted } from '@/utils/ticketDeletedState';
 *
 *   const active = tickets.filter(isTicketActive);
 *   const justDeletedOnes = tickets.filter(isTicketDeleted);
 *
 * NOT YET ADOPTED — DMC-D2 is the additive helpers + RLS hardening
 * pass. Caller migration (replacing scattered `.is('deleted_at',
 * null)` / `.neq('status', 'Deleted')` filters with these helpers
 * where appropriate) is DMC-D3.
 */

/**
 * Minimal shape: anything with optional `status` and `deleted_at`
 * fields. Intentionally permissive so callers can pass partial
 * `Ticket` rows, snapshot rows, query-projection rows, etc., without
 * structural-type widening at every call site.
 */
export interface DeletedStateTicketShape {
  status?: string | null;
  // string from the DB row; Date after in-memory parsing. Subtype shapes carry
  // `string | Date | null`, so the base must admit both to be legally extended.
  deleted_at?: string | Date | null;
}

/**
 * Canonical deleted-state predicate.
 *
 * Returns `true` when EITHER soft-delete representation is set:
 *   - `deleted_at` is non-null (the post-DMC-D1 canonical column), OR
 *   - `status === 'Deleted'` (the legacy text representation).
 *
 * Mirrors SQL `public.is_ticket_deleted(public.tickets)`.
 */
export function isTicketDeleted(ticket: DeletedStateTicketShape): boolean {
  return ticket.deleted_at != null || ticket.status === 'Deleted';
}

/**
 * Canonical active-state predicate.
 *
 * Returns `true` ONLY when neither soft-delete representation is set:
 *   - `deleted_at` is null/undefined, AND
 *   - `status` is anything other than `'Deleted'` (including null /
 *     undefined / 'Active' / 'Paused' / 'Completed' / 'Cancelled' /
 *     'Blocked' / etc.).
 *
 * Mirrors SQL `public.is_ticket_active(public.tickets)`.
 *
 * Note: this is the logical complement of `isTicketDeleted` for any
 * ticket with a defined status. For tickets with `status` undefined or
 * null, both helpers can return non-equivalent values:
 *   - `isTicketDeleted({ deleted_at: null, status: null })` → false
 *   - `isTicketActive({ deleted_at: null, status: null })`  → true
 *   In practice `tickets.status` is NOT NULL in the DB schema; the
 *   permissive type allows partial rows without forcing every caller
 *   to assert non-null.
 */
export function isTicketActive(ticket: DeletedStateTicketShape): boolean {
  return ticket.deleted_at == null && ticket.status !== 'Deleted';
}
